import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, CircularProgress, Divider, Drawer, IconButton, Stack, TextField, Typography } from "@mui/material";
import { FormEvent, useEffect, useState } from "react";

import { formatCpf } from "utils/federal-tax-id";
import { mensagemDoBackend } from "v2/pages/Workspace/Activity/ErroAoCarregar";

import { abrirHistorico, buscarPessoas, buscarPorMotivo } from "./buscaPessoa.service";
import { IBuscaPessoasResposta, IContratoCartao, IHistoricoResposta, IPessoaCartao } from "./types";

/** Só quando o backend não respondeu; qualquer frase sobre a pessoa vem dele. */
const FALHA_REDE = "Não conseguimos responder agora. Tente de novo em instantes.";
const dataBr = (iso: string | null): string => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
// O backend serializa o HttpResponse inteiro ({ statusCode, data: { message } }); o helper lê as formas todas.
const mensagemDe = (err: unknown): string => mensagemDoBackend(err) ?? FALHA_REDE;

function Historico({ cpf, workspaceId }: { cpf: string; workspaceId: string }) {
  const [estado, setEstado] = useState<"fechado" | "carregando" | "pronto">("fechado");
  const [dados, setDados] = useState<IHistoricoResposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function abrir() {
    setEstado("carregando");
    setErro(null);
    try {
      setDados(await abrirHistorico(cpf, workspaceId));
      setEstado("pronto");
    } catch (err) {
      // A frase do backend pode pedir para tentar de novo: o botão volta.
      setErro(mensagemDe(err));
      setEstado("fechado");
    }
  }

  if (estado === "fechado") {
    return (
      <>
        {erro && <Alert severity="warning">{erro}</Alert>}
        <Button size="small" onClick={abrir} sx={{ alignSelf: "flex-start" }}>
          Ver histórico
        </Button>
      </>
    );
  }
  if (estado === "carregando") return <CircularProgress size={20} />;
  return (
    <Stack spacing={0.5} sx={{ pl: 1, borderLeft: "2px solid", borderColor: "grey.300" }}>
      {dados?.motivo && <Alert severity="info">{dados.motivo}</Alert>}
      {dados?.eventos.map((e, i) => (
        <Box key={`${e.evento}-${e.data}-${i}`}>
          <Typography variant="body2" fontWeight={600}>
            {e.evento}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[e.data, e.detalhe].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function Contrato({ cpf, c }: { cpf: string; c: IContratoCartao }) {
  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {c.rotuloContrato}
      </Typography>
      <Typography variant="body2">{c.frase}</Typography>
      {c.proximaAcao && <Typography variant="caption">{c.proximaAcao}</Typography>}
      {c.linha.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {c.linha.map((m) => [m.rotulo, dataBr(m.data)].filter(Boolean).join(" ")).join(" > ")}
        </Typography>
      )}
      {cpf && <Historico cpf={cpf} workspaceId={c.workspaceId} />}
    </Stack>
  );
}

function Cartao({ p, nivel = 0 }: { p: IPessoaCartao; nivel?: number }) {
  return (
    <Box sx={{ pl: nivel * 2, py: 1 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {p.nome}
      </Typography>
      {p.cpf && (
        <Typography variant="caption" color="text.secondary">
          {formatCpf(p.cpf)}
        </Typography>
      )}
      {p.contratos.map((c) => (
        <Contrato key={c.workspaceId} cpf={p.cpf} c={c} />
      ))}
      {p.dependentes.map((d) => (
        <Cartao key={`${d.cpf || d.nome}-${d.cpfTitular}`} p={d} nivel={nivel + 1} />
      ))}
    </Box>
  );
}

/** Pedido vindo da aba Hoje: a linha de "Fora do caminho comum" clicada. `seq` muda a cada clique. */
export interface IPedidoPorMotivo {
  motivo: string;
  rotulo: string;
  seq: number;
}

/** "Onde está esta pessoa?" (spec do RH §7). Toda frase sobre a pessoa vem pronta do backend. */
export function BuscaPessoa({ pedido = null }: { pedido?: IPedidoPorMotivo | null }) {
  const [termo, setTermo] = useState("");
  const [aberto, setAberto] = useState(false);
  const [titulo, setTitulo] = useState("Onde está esta pessoa?");
  const [carregando, setCarregando] = useState(false);
  const [resposta, setResposta] = useState<IBuscaPessoasResposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function carregar(consulta: () => Promise<IBuscaPessoasResposta>, cabecalho: string) {
    setTitulo(cabecalho);
    setAberto(true);
    setCarregando(true);
    setErro(null);
    setResposta(null);
    try {
      setResposta(await consulta());
    } catch (err) {
      setErro(mensagemDe(err));
    } finally {
      setCarregando(false);
    }
  }

  async function buscar(e: FormEvent) {
    e.preventDefault();
    if (termo.trim().length < 3) return;
    await carregar(() => buscarPessoas(termo), "Onde está esta pessoa?");
  }

  useEffect(() => {
    if (pedido) carregar(() => buscarPorMotivo(pedido.motivo), pedido.rotulo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido?.seq]);

  return (
    <>
      <Box component="form" onSubmit={buscar} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          id="busca-pessoa-termo"
          size="small"
          label="CPF ou nome"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
        />
        <Button type="submit" variant="contained" disabled={termo.trim().length < 3}>
          Buscar
        </Button>
      </Box>
      <Drawer anchor="right" open={aberto} onClose={() => setAberto(false)}>
        <Box sx={{ width: { xs: "100vw", sm: 480 }, p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{titulo}</Typography>
            <IconButton aria-label="Fechar" onClick={() => setAberto(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {resposta?.atualizado && (
            <Typography variant="caption" color="text.secondary">
              {resposta.atualizado}
            </Typography>
          )}
          <Divider sx={{ my: 1 }} />
          {carregando && <CircularProgress size={24} />}
          {erro && <Alert severity="warning">{erro}</Alert>}
          {resposta?.mensagem && <Alert severity="info">{resposta.mensagem}</Alert>}
          {resposta?.itens.map((p) => (
            <Cartao key={`${p.cpf || p.nome}-${p.cpfTitular}`} p={p} />
          ))}
        </Box>
      </Drawer>
    </>
  );
}
